import { FC, useCallback, useState } from 'react';

import { useDismissibleSelection } from '@/hooks';
import { OverflowFadeScrollArea, SearchSiderToggleRow } from '@/shared';
import {
    DEFAULT_AVAILABLE_COMPANY_RESULT_COLUMNS,
    DEFAULT_COMPANY_RESULT_COLUMNS,
} from '@/shared/components/company-search/columns';

import ActionsRow, { CompanyActionResult } from './layout/01-ActionsRow';
import ResultsList from './layout/02-ResultsList';

interface CompanyResultsProps {
    companyResults: CompanyActionResult[];
    groupRelatedListings: boolean;
    leftSiderCollapsed: boolean;
    onApplyColumnFilter: (column: string, result: unknown) => void;
    onToggleLeftSider: () => void;
    onToggleRightSider: () => void;
    page: number;
    resultsPerPage: number;
    rightSiderCollapsed: boolean;
    serverPaginated: boolean;
}

const CompanyResults: FC<CompanyResultsProps> = ({
    companyResults,
    groupRelatedListings,
    leftSiderCollapsed,
    onApplyColumnFilter,
    onToggleLeftSider,
    onToggleRightSider,
    page,
    resultsPerPage,
    rightSiderCollapsed,
    serverPaginated,
}): JSX.Element => {
    const [availableColumns] = useState<string[]>([...DEFAULT_AVAILABLE_COMPANY_RESULT_COLUMNS]);
    const [listColumns, setListViewColumns] = useState<string[]>([...DEFAULT_COMPANY_RESULT_COLUMNS]);
    const [resultsSelected, setResultsSelected] = useState<Set<string>>(new Set());
    const [checkAll, setCheckAll] = useState(false);
    const [indeterminate, setIndeterminate] = useState(false);
    const clearSelection = useCallback(() => {
        setResultsSelected(new Set());
        setCheckAll(false);
        setIndeterminate(false);
    }, []);

    useDismissibleSelection({ active: resultsSelected.size > 0, onDismiss: clearSelection });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <SearchSiderToggleRow
                leftSiderCollapsed={leftSiderCollapsed}
                onToggleLeftSider={onToggleLeftSider}
                onToggleRightSider={onToggleRightSider}
                rightSiderCollapsed={rightSiderCollapsed}
                style={{ marginBottom: 0 }}
            >
                <ActionsRow
                    results={companyResults}
                    resultsSelected={resultsSelected}
                    setCheckAll={setCheckAll}
                    setIndeterminate={setIndeterminate}
                    setResultsSelected={setResultsSelected}
                />
            </SearchSiderToggleRow>
            <OverflowFadeScrollArea
                aria-label='Company search results'
                containerStyle={{ flex: '1 1 0%', minHeight: 0 }}
                surface='container'
            >
                <ResultsList
                    availableColumns={availableColumns}
                    checkAll={checkAll}
                    groupRelatedListings={groupRelatedListings}
                    indeterminate={indeterminate}
                    limit={resultsPerPage}
                    listColumns={listColumns}
                    onApplyColumnFilter={onApplyColumnFilter}
                    page={page}
                    results={companyResults}
                    resultsSelected={resultsSelected}
                    setCheckAll={setCheckAll}
                    setIndeterminate={setIndeterminate}
                    setListViewColumns={setListViewColumns}
                    setResultsSelected={setResultsSelected}
                    serverPaginated={serverPaginated}
                />
            </OverflowFadeScrollArea>
        </div>
    );
};

export default CompanyResults;
